/* eslint-disable no-underscore-dangle */
import 'es6-promise/auto';
import 'isomorphic-fetch';
import 'core-js/stable';
import '@honda-canada/design-system-react/lib/motors-css.css';
import '@splidejs/splide/dist/css/themes/splide-default.min.css';

import AppRootWithUserLocation from '@honda-canada/sitecore-jss-common/lib/components/AppRootWithUserLocation';
import BottomElementProvider from '@honda-canada/sitecore-jss-components/lib/components/Footer/service/BottomElementProvider';
import ErrorBoundary from '@honda-canada/sitecore-jss-common/lib/components/ErrorBoundary';
import LocalStorageProvider from '@honda-canada/sitecore-jss-components/lib/components/LocalStorageContext';
import ModelFiltersContextProvider from '@honda-canada/sitecore-jss-components/lib/components/ModelFiltersContext';
import PopupContextProvider from '@honda-canada/sitecore-jss-components/lib/components/Popup/PopupContext';
import ReactDOM from 'react-dom';
import ReducersProvider from '@honda-canada/sitecore-jss-components/lib/components/ReducersProvider';
import TagManager from 'react-gtm-module';
import i18nInit from '@honda-canada/sitecore-jss-common/lib/utils/i18nInit';
import isBrowserModern from '@honda-canada/sitecore-jss-common/lib/utils/isBrowserModern';
import React, { useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { MotorsTheme as Theme } from '@honda-canada/design-system-react/lib/motors-theme';
import { store } from '@honda-canada/sitecore-jss-components/lib/core/store';

import ApplicationErrorMessage from './ApplicationErrorMessage';
import MainContainer from './MainContainer.tsx';
import componentFactory from './componentFactory';
import updateLanguageLink from './updateLanguageLinks';

let renderFunction = ReactDOM.render;

let initLanguage = 'en';

let __JSS_STATE__ = null;
const ssrRawJson = document.getElementById('__JSS_STATE__');

if (ssrRawJson) {
  __JSS_STATE__ = JSON.parse(ssrRawJson.innerHTML);
}

if (__JSS_STATE__) {
  renderFunction = ReactDOM.hydrate;
  initLanguage = __JSS_STATE__.sitecore.context.language;
}

const { config } = __JSS_STATE__;

const gtmDevParameters = new URLSearchParams(config.gtmDevParameters);
const tagManagerArgs = config.gtmDevParameters
  ? {
      gtmId: 'GTM-PVKMGS',
      auth: gtmDevParameters && gtmDevParameters.get('gtm_auth'),
      preview: gtmDevParameters && gtmDevParameters.get('gtm_preview'),
    }
  : {
      gtmId: 'GTM-PVKMGS',
    };

TagManager.initialize(tagManagerArgs);

const App = () => {
  const [reloadLocationFromCookies, setReloadLocationFromCookies] = useState(false);

  return (
    <ErrorBoundary errorMessage={<ApplicationErrorMessage />}>
      <ReduxProvider store={store}>
        <HelmetProvider>
          <AppRootWithUserLocation
            path={window.location.pathname}
            Router={BrowserRouter}
            ReducersProvider={ReducersProvider}
            ssrState={__JSS_STATE__}
            updateExternalLanguageLinks={(language, routeLanguageSelectors) => {
              updateLanguageLink('#toggleUrl', language, routeLanguageSelectors);
              updateLanguageLink('.global-header-compressed-link.language', language, routeLanguageSelectors);
            }}
            config={config}
            errorMessage={<ApplicationErrorMessage is404 />}
            activePlaceholders={['main', 'footer', 'header']}
            modelFiltersContextProvider={ModelFiltersContextProvider}
            bottomElementProvider={BottomElementProvider}
            localStorageProvider={LocalStorageProvider}
            reloadLocationFromCookies={reloadLocationFromCookies}
            setReloadLocationFromCookies={setReloadLocationFromCookies}
            popupContextProvider={PopupContextProvider}
            theme={Theme}
            mainContainer={MainContainer}
            componentFactory={componentFactory}
          />
        </HelmetProvider>
      </ReduxProvider>
    </ErrorBoundary>
  );
};

const renderApp = async () => {
  const dictionary = __JSS_STATE__?.viewBag?.dictionary;
  const rootElement = document.getElementById('jss-root');
  await i18nInit(initLanguage, dictionary, config);

  renderFunction(<App />, rootElement);
};

if (isBrowserModern()) {
  renderApp();
} else {
  import('./polyfills').then(renderApp);
}
